<script setup>
  import {computed, onBeforeMount, ref, watch} from "vue";

  import ComponentDosageInput from "@components/Inputs/DosageInput.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import ComponentSwitch from "@components/Switch.vue";
  import ComponentTextarea from "@components/Textarea/Textarea.vue";
  import {medicationMeal, medicationUsage} from "@enums/medicationEnums.js";
  import {usePage} from "@inertiajs/vue3";
  import MedicationComponentDetailDialogAlternativeDosageData from "@pages/Records/Components/Sections/MedicationBar/Components/DetailDialog/Components/AlternativeDosageData.vue";
  import MedicationComponentDetailDialogSatisfactionField from "@pages/Records/Components/Sections/MedicationBar/Components/DetailDialog/Components/SatisfactionField.vue";
  import MedicationComponentIndicationDialog from "@pages/Records/Components/Sections/MedicationBar/Components/Indications/IndicationDialog.vue";
  import MedicationComponentIndicationDisplay from "@pages/Records/Components/Sections/MedicationBar/Components/Indications/IndicationDisplay.vue";
  import {checkDeviantMedicationDetailDialog} from "@pages/Records/Components/Sections/MedicationBar/Utils/medicationConsistencyCheck.js";
  import {complianceCheck} from "@pages/Records/Helper/complianceCheck.js";
  import * as dosageHelper from "@pages/Records/Helper/dosageHelper.js";
  import {MedCheckbox, MedFormField, MedIcon, MedInfoButton, MedLabel} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";
  import {usePrivacyGuard} from "@utils/Helpers/PrivacyGuard.js";
  import {cloneDeep, isEqual, pick} from "es-toolkit";

  defineOptions({
    name: "MedicationComponentDetailDialogNonComplianceData",
  });

  const props = defineProps({
    isEditable: {
      type: Boolean,
      default: false,
    },
    medicationForm: {
      type: Object,
      required: true,
    },
  });

  const emit = defineEmits(["changeForm", "valid", "invalid", "save"]);

  const alternativeDosageId = "deviantMedicationFormAlternativeDosageValidatorId";

  const page = usePage();
  const {validatePrivacyObject} = usePrivacyGuard();

  const refAlternativeDosage = ref(null);
  const refIndicationMenuDialog = ref(null);

  const showComplianceData = ref(false);
  const isAlternativeDosage = ref(false);
  const invalidDosages = ref([]);

  const storage = ref(false);
  const expired = ref(false);
  const wrongUse = ref(false);

  const deviantMedicationForm = ref(props.medicationForm.value.deviantmedication);
  const additionalInformationForm = ref(props.medicationForm.value.additionalinformation);

  // computed instead of a value copied in onBeforeMount, so the unit carried
  // over from the medication (see deviantUnitKeys in DetailDialog) is actually
  // shown here instead of leaving the old one on screen as a fake deviation
  const currentDosageUnit = computed(() =>
    deviantMedicationForm.value?.dosage ? dosageHelper.getCurrentDosageUnit(deviantMedicationForm.value.dosage) : null,
  );
  const currentDosageUnitList = computed(() => dosageHelper.getDosageUnitList(props.medicationForm.value.core));

  watch(
    () => isAlternativeDosage.value,
    (newValue) => {
      if (newValue === false) {
        emit("valid", alternativeDosageId);
        restoreDefaultDosage();
      } else {
        if (refAlternativeDosage.value) {
          refAlternativeDosage.value.clearDefaultDosage();
        }
      }
    },
  );

  watch(
    () => [
      props.medicationForm.value.dosage,
      deviantMedicationForm.value,
      additionalInformationForm.value.satisfaction,
      additionalInformationForm.value.sideeffects,
    ],
    () => {
      const sortedDeviantMedication = cloneDeep(deviantMedicationForm.value);
      const sortedPropertiesMedication = cloneDeep(props.medicationForm.value);

      const checkIndicationUnknown =
        deviantMedicationForm.value.indication_unknown === null ||
        deviantMedicationForm.value.indication_unknown === 0 ||
        deviantMedicationForm.value.indication_unknown === props.medicationForm.value.indication_unknown;

      const checkIndications =
        sortedDeviantMedication.indications.length === 0 ||
        isEqual(
          sortedDeviantMedication.indications.map((indication) => indication.name).toSorted(),
          sortedPropertiesMedication.indications.map((indication) => indication.name).toSorted(),
        );

      const checkUsage = isEqualOrNull(deviantMedicationForm.value.usage, props.medicationForm.value.usage);

      const checkMeal = isEqualOrNull(
        deviantMedicationForm.value.dosage?.meal,
        props.medicationForm.value.dosage?.meal,
      );

      const checkDosageAmount = checkDosage(deviantMedicationForm.value.dosage, props.medicationForm.value.dosage);

      const checkInfoUnitKeyDoe = comparePickedObjects(
        pick(deviantMedicationForm.value.dosage, ["selected_unit", "selected_unit_key_doe", "info"]),
        pick(props.medicationForm.value.dosage, ["selected_unit", "selected_unit_key_doe", "info"]),
      );

      const checkSatisfaction = additionalInformationForm.value.satisfaction === null;

      const checkSideEffects =
        additionalInformationForm.value.sideeffects === null ||
        additionalInformationForm.value.sideeffects.length === 0;

      if (
        additionalInformationForm.value.compliance !== false &&
        (!checkIndications ||
          !checkIndicationUnknown ||
          !checkUsage ||
          !checkMeal ||
          !checkInfoUnitKeyDoe ||
          !checkDosageAmount ||
          !checkSatisfaction ||
          !checkSideEffects)
      ) {
        additionalInformationForm.value.compliance = false;
        updateNonCompliance();
      } else if (
        additionalInformationForm.value.compliance !== true &&
        checkIndications &&
        checkIndicationUnknown &&
        checkUsage &&
        checkMeal &&
        checkInfoUnitKeyDoe &&
        checkDosageAmount &&
        checkSatisfaction &&
        checkSideEffects
      ) {
        additionalInformationForm.value.compliance = true;
        updateNonCompliance();
      }
    },
    {deep: true},
  );

  watch(
    () => props.medicationForm.value.additionalinformation,
    () => {
      additionalInformationForm.value = props.medicationForm.value.additionalinformation;
    },
  );

  onBeforeMount(() => {
    if (
      deviantMedicationForm.value.dosage &&
      deviantMedicationForm.value.dosage.dosagealternative !== null &&
      deviantMedicationForm.value.dosage.dosagealternative?.id !== null
    ) {
      isAlternativeDosage.value = true;
    }

    if (deviantMedicationForm.value && deviantMedicationForm.value.id === null) {
      deviantMedicationForm.value.usage = null;
    }

    if (
      additionalInformationForm.value.sideeffects !== null &&
      additionalInformationForm.value.sideeffects.length > 0
    ) {
      storage.value = additionalInformationForm.value.sideeffects.includes("storage");
      expired.value = additionalInformationForm.value.sideeffects.includes("expired");
      wrongUse.value = additionalInformationForm.value.sideeffects.includes("wronguse");
    }

    showComplianceData.value = checkDeviantMedicationDetailDialog(props.medicationForm.value);
  });

  function comparePickedObjects(object1, object2) {
    for (const key in object1) {
      if (!isEqualOrNull(object1[key], object2[key])) {
        return false;
      }
    }

    return true;
  }

  function isEqualOrNull(valueA, valueB) {
    return valueA === null || valueA === valueB;
  }

  const checkDosage = (deviationDosage, propsDosage) => {
    if (
      deviationDosage.morning === null &&
      deviationDosage.noon === null &&
      deviationDosage.evening === null &&
      deviationDosage.night === null
    ) {
      return true;
    }

    const deviationOneLineDosage =
      (deviationDosage.morning?.toString() ?? 0) +
      (deviationDosage.noon?.toString() ?? 0) +
      (deviationDosage.evening?.toString() ?? 0) +
      (deviationDosage.night?.toString() ?? 0);

    const propsOneLineDosage =
      (propsDosage.morning?.toString() ?? 0) +
      (propsDosage.noon?.toString() ?? 0) +
      (propsDosage.evening?.toString() ?? 0) +
      (propsDosage.night?.toString() ?? 0);

    if (deviationOneLineDosage === "0000" || deviationOneLineDosage === propsOneLineDosage) {
      return true;
    }

    return false;
  };

  const handleInvalidDosage = (dosageId) => {
    emit("invalid", dosageId);
  };

  const updateUsage = () => {
    if (deviantMedicationForm.value.usage !== null) {
      deviantMedicationForm.value.usage = Number(deviantMedicationForm.value.usage);
    }

    emit("changeForm", {key: "deviantmedication.usage", newValue: deviantMedicationForm.value.usage});
  };

  const updateDosage = (newValues, type) => {
    const asFraction = type + "_as_fraction";

    dosageHelper.updateDosage(deviantMedicationForm.value.dosage, type, newValues, () => {
      emit("changeForm", {
        key: "deviantmedication.dosage." + type,
        newValue: deviantMedicationForm.value.dosage[type],
      });

      emit("changeForm", {
        key: "deviantmedication.dosage." + asFraction,
        newValue: deviantMedicationForm.value.dosage[asFraction],
      });

      emit("valid", newValues.id);
    });
  };

  const updateDosageUnit = ({keyDoe, unit}) => {
    dosageHelper.updateDosageUnit(deviantMedicationForm.value.dosage, keyDoe, unit, () => {
      emit("changeForm", {
        key: "deviantmedication.dosage.selected_unit",
        newValue: deviantMedicationForm.value.dosage.selected_unit,
      });

      emit("changeForm", {
        key: "deviantmedication.dosage.selected_unit_key_doe",
        newValue: deviantMedicationForm.value.dosage.selected_unit_key_doe,
      });
    });
  };

  const updateMeal = () => {
    emit("changeForm", {key: "deviantmedication.dosage.meal", newValue: deviantMedicationForm.value.dosage.meal});
  };

  const checkErrors = () => {
    if (invalidDosages.value.length > 0) {
      emit("invalid", alternativeDosageId);
    } else {
      emit("valid", alternativeDosageId);
    }
  };

  const handleInvalidAlternativeDosageEvent = (dosageId) => {
    emit("invalid", dosageId);
    checkErrors();
  };

  const handleValidAlternativeDosageEvent = (dosageId) => {
    emit("valid", dosageId);
    checkErrors();
  };

  const handleAlternativeDosageIdsChanged = () => {
    invalidDosages.value = [];
    checkErrors();
  };

  const handleAlternativeDosageTypeChange = () => {
    checkErrors();
  };

  const handleDosageChange = (newDosage) => {
    emit("changeForm", {key: "deviantmedication.dosage", newValue: newDosage});
  };

  const handleSatisfaction = () => {
    const newValue = additionalInformationForm.value.satisfaction;

    if (!newValue) {
      handleSatisfactionComment(null);
    }

    emit("changeForm", {
      key: "additionalinformation.satisfaction",
      newValue: newValue,
    });
  };

  const handleSatisfactionComment = (newSatisfactionComment) => {
    additionalInformationForm.value.satisfactioncomment = newSatisfactionComment;

    emit("changeForm", {
      key: "additionalinformation.satisfactioncomment",
      newValue: additionalInformationForm.value.satisfactioncomment,
    });
  };

  const addSymptom = (newSymptomData) => {
    emit("changeForm", {
      key: "additionalinformation.satisfactionSymptom",
      newValue: newSymptomData,
    });
  };

  const handleSideEffects = (value, type) => {
    if (value) {
      addToList(type);
    } else {
      removeFromList(type);
    }

    const pageData = page.props.medications?.data.find((medication) => medication.id === props.medicationForm.value.id);

    complianceCheck(props.medicationForm.value, pageData, (isCompliance) => {
      if (additionalInformationForm.value.compliance && !isCompliance) {
        additionalInformationForm.value.compliance = false;
        updateNonCompliance();
      }
    });
  };

  const addToList = (sourceName) => {
    if (!additionalInformationForm.value.sideeffects.includes(sourceName)) {
      additionalInformationForm.value.sideeffects.push(sourceName);

      emit("changeForm", {
        key: "additionalinformation.sideeffects",
        newValue: additionalInformationForm.value.sideeffects,
      });
    }
  };

  const removeFromList = (sourceName) => {
    const indexFromList = additionalInformationForm.value.sideeffects.indexOf(sourceName);

    if (indexFromList !== -1) {
      additionalInformationForm.value.sideeffects.splice(indexFromList, 1);

      emit("changeForm", {
        key: "additionalinformation.sideeffects",
        newValue: additionalInformationForm.value.sideeffects,
      });
    }
  };

  const updateComplianceInfo = () => {
    emit("changeForm", {key: "deviantmedication.dosage.info", newValue: deviantMedicationForm.value.dosage.info});
  };

  const updateNonCompliance = () => {
    emit("changeForm", {
      key: "additionalinformation.compliance",
      newValue: additionalInformationForm.value.compliance,
    });
  };

  const changeForm = ({key, newValue}) => {
    deviantMedicationForm.value[key] = newValue;

    emit("changeForm", {key: "deviantmedication." + key, newValue: newValue});
  };

  const openIndicationDialog = () => {
    if (props.isEditable) {
      refIndicationMenuDialog.value.open({
        medication: deviantMedicationForm.value,
        medicationKeyFam: props.medicationForm.value.abdata_key_fam,
        medicationHashId: props.medicationForm.value.hashId,
      });
    }
  };

  const restoreDefaultDosage = () => {
    const oldData = page.props.medications?.data.find((medication) => medication.id === props.medicationForm.value.id);

    const dosage = deviantMedicationForm.value?.dosage ?? {};

    dosage.dosagealternative = {};

    if (oldData.deviantmedication !== null && oldData.deviantmedication.dosage !== null) {
      dosage.morning = oldData.deviantmedication.dosage?.morning;
      dosage.morning_as_fraction = oldData.deviantmedication.dosage?.morning_as_fraction;

      dosage.noon = oldData.deviantmedication.dosage?.noon;
      dosage.noon_as_fraction = oldData.deviantmedication.dosage?.noon_as_fraction;

      dosage.evening = oldData.deviantmedication.dosage?.evening;
      dosage.evening_as_fraction = oldData.deviantmedication.dosage?.evening_as_fraction;

      dosage.night = oldData.deviantmedication.dosage?.night;
      dosage.night_as_fraction = oldData.deviantmedication.dosage?.night_as_fraction;
    }

    handleDosageChange(dosage);
  };

  const resetDeviationIndication = () => {
    const medication = cloneDeep(
      page.props.medications?.data.find((oldMedication) => oldMedication.id === props.medicationForm.value.id),
    );

    if (medication.deviantmedication === null) {
      changeForm({key: "indications", newValue: []});
      changeForm({key: "indication_unknown", newValue: 0});
    } else {
      changeForm({
        key: "indications",
        newValue: medication.deviantmedication.indications,
      });

      changeForm({
        key: "indication_unknown",
        newValue: medication.deviantmedication.indication_unknown,
      });
    }
  };

  const privacyErrors = computed(() => {
    return validatePrivacyObject(deviantMedicationForm.value?.dosage, {
      info: "Hinweise zur abweichenden Anwendung",
    });
  });
</script>

<template>
  <button
    :class="{'border-b': !showComplianceData}"
    class="accordion-header flex w-full cursor-pointer border-gray-200 bg-white bg-[linear-gradient(0deg,#fff,#f0f0f0)] py-3 text-left text-base text-gray-800"
    :data-test="getTestId('compliance-accordion-header')"
    @click="showComplianceData = !showComplianceData"
  >
    <div class="flex items-center font-medium">
      <MedIcon
        size="lg"
        icon="keyboard_arrow_down"
        :class="
          cn('transition-transform duration-200', {
            'rotate-180': showComplianceData,
          })
        "
      />

      <div class="flex items-center space-x-1">
        <span>Patientenangaben</span>
        <span @click.stop @mousedown.stop>
          <MedInfoButton
            size="lg"
            side="right"
            aria-label="Patientenangaben Info"
            disable-portal
            :data-test="getTestId('non-compliance-info-button')"
          >
            <p class="text-sm">
              z.B. abweichendes Verhalten, Unzufriedenheit etc.
              <br />
              (für automatisierte Adhärenz-Prüfung)
            </p>
          </MedInfoButton>
        </span>
      </div>
    </div>
  </button>

  <div v-if="showComplianceData" class="grid grid-cols-12 gap-x-2 gap-y-4 p-2">
    <!-- medication fields -->
    <div class="col-span-4 md:col-span-2">
      <ComponentSelect
        :id="'usage' + deviantMedicationForm.id"
        ref="usage"
        v-model="deviantMedicationForm.usage"
        label="Art"
        :name="'usage' + deviantMedicationForm.id"
        :disabled="!isEditable"
        :nullable="true"
        :options="medicationUsage"
        :test-id="getTestId('compliance-usage')"
        @change="updateUsage"
      />
    </div>

    <div class="col-span-4 mx-auto md:col-span-2">
      <p class="text-sm text-gray-500">Dosierung</p>
      <div class="mt-1 flex gap-1">
        <ComponentDosageInput
          :id="'complianceDataDosageMorning' + deviantMedicationForm.id"
          :name="'complianceDataDosageMorning' + deviantMedicationForm.id"
          :value="deviantMedicationForm.dosage?.morning"
          :as-fraction="deviantMedicationForm.dosage?.morning_as_fraction"
          :disabled="!isEditable || !!deviantMedicationForm?.dosage?.dosagealternative?.type"
          :test-id="getTestId('compliance-morning')"
          @input="updateDosage($event, 'morning')"
          @is-invalid="handleInvalidDosage"
        />

        <ComponentDosageInput
          :id="'complianceDataDosageNoon' + deviantMedicationForm.id"
          :name="'complianceDataDosageNoon' + deviantMedicationForm.id"
          :value="deviantMedicationForm.dosage?.noon"
          :as-fraction="deviantMedicationForm.dosage?.noon_as_fraction"
          :disabled="!isEditable || !!deviantMedicationForm?.dosage?.dosagealternative?.type"
          :test-id="getTestId('compliance-noon')"
          @input="updateDosage($event, 'noon')"
          @is-invalid="handleInvalidDosage"
        />

        <ComponentDosageInput
          :id="'complianceDataDosageEvening' + deviantMedicationForm.id"
          :name="'complianceDataDosageEvening' + deviantMedicationForm.id"
          :value="deviantMedicationForm.dosage?.evening"
          :as-fraction="deviantMedicationForm.dosage?.evening_as_fraction"
          :disabled="!isEditable || !!deviantMedicationForm?.dosage?.dosagealternative?.type"
          :test-id="getTestId('compliance-evening')"
          @input="updateDosage($event, 'evening')"
          @is-invalid="handleInvalidDosage"
        />

        <ComponentDosageInput
          :id="'complianceDataDosageNight' + deviantMedicationForm.id"
          :name="'complianceDataDosageNight' + deviantMedicationForm.id"
          :value="deviantMedicationForm.dosage?.night"
          :as-fraction="deviantMedicationForm.dosage?.night_as_fraction"
          :disabled="!isEditable || !!deviantMedicationForm?.dosage?.dosagealternative?.type"
          :test-id="getTestId('compliance-night')"
          @input="updateDosage($event, 'night')"
          @is-invalid="handleInvalidDosage"
        />
      </div>
    </div>

    <div class="col-span-4 md:col-span-2">
      <ComponentSelect
        :id="'dosageUnit' + deviantMedicationForm.id"
        ref="unitfield"
        :model-value="currentDosageUnit"
        label="Einheit"
        :data-hash-id="deviantMedicationForm.id"
        :disabled="!isEditable"
        :options="currentDosageUnitList"
        key-value="keyDoe"
        key-name="unit"
        return-type="object"
        :test-id="getTestId('compliance-unit')"
        @change="updateDosageUnit"
      />
    </div>

    <div class="col-span-6 md:col-span-3">
      <ComponentSelect
        :id="'meal' + deviantMedicationForm.id"
        ref="meal"
        v-model="deviantMedicationForm.dosage.meal"
        :name="'meal' + deviantMedicationForm.id"
        :disabled="!isEditable"
        label="Mahlzeit"
        key-name="name"
        :nullable="true"
        :options="medicationMeal"
        :test-id="getTestId('compliance-meal')"
        @change="updateMeal"
      />
    </div>

    <div class="col-span-6 md:col-span-3">
      <MedicationComponentIndicationDisplay
        show-label
        :indications="deviantMedicationForm.indications"
        :indications-unknown="deviantMedicationForm.indication_unknown"
        :is-active="isEditable"
        :is-discontinued="false"
        :disabled="!isEditable"
        @open-dialog="openIndicationDialog"
      />

      <template v-if="isEditable">
        <MedicationComponentIndicationDialog
          ref="refIndicationMenuDialog"
          :is-detail-dialog="true"
          @form-change="changeForm"
          @close="resetDeviationIndication"
        />
      </template>
    </div>
    <!-- end medication fields -->

    <!-- alternative dosage -->
    <div class="col-span-12 col-start-1 flex h-9 items-center md:col-span-3 lg:col-span-2">
      <MedFormField orientation="horizontal">
        <MedCheckbox
          v-model="isAlternativeDosage"
          :disabled="!isEditable"
          variant="accent"
          :data-test="getTestId('compliance-alternative-dosage-checkbox')"
        />

        <MedLabel>Sonderdosierung</MedLabel>
      </MedFormField>
    </div>

    <div class="col-span-12 -mt-4 md:col-span-9 md:mt-0 lg:col-span-10">
      <MedicationComponentDetailDialogAlternativeDosageData
        v-if="isAlternativeDosage"
        ref="refAlternativeDosage"
        class="ml-6 md:ml-0"
        :medication-form="deviantMedicationForm"
        :is-editable="isEditable"
        @is-invalid="handleInvalidAlternativeDosageEvent"
        @is-valid="handleValidAlternativeDosageEvent"
        @alternative-dosage-ids-changed="handleAlternativeDosageIdsChanged"
        @change="handleAlternativeDosageTypeChange"
        @init-type="handleAlternativeDosageTypeChange"
        @dosage-change="handleDosageChange"
      />
    </div>
    <!-- end alternative dosage -->

    <!-- satisfaction -->
    <div class="col-span-6 col-start-1 md:col-span-3">
      <ComponentSelect
        id="satisfaction"
        ref="satisfaction"
        v-model="additionalInformationForm.satisfaction"
        label="Unzufrieden aufgrund von"
        name="satisfaction"
        :nullable="true"
        :disabled="!isEditable"
        :test-id="getTestId('compliance-satisfaction')"
        @change="handleSatisfaction"
      >
        <option value="uaw">Unzufrieden wegen UAW / NW</option>
        <option value="effectiveness">Unzufrieden mit Wirksamkeit</option>
        <option value="handling">Unzufrieden mit Handhabung</option>
      </ComponentSelect>
    </div>

    <div class="col-span-6 md:col-span-6">
      <p class="mb-1 truncate text-sm text-gray-500">Details zur Unzufriedenheit</p>

      <MedicationComponentDetailDialogSatisfactionField
        ref="satisfactionComment"
        :clearable="true"
        :key-fam="medicationForm.value.abdata_key_fam"
        :row-id="1"
        :additional-information-form="additionalInformationForm"
        :disabled="!isEditable"
        :test-id="getTestId('compliance')"
        @update-satisfaction-value="handleSatisfactionComment"
        @add-symptom="addSymptom"
      />
    </div>
    <!-- end satisfaction -->

    <!-- side effects -->
    <fieldset class="col-span-9 col-start-1">
      <legend class="text-sm text-gray-500">Lagerung / Verfall / Anwendung</legend>
      <div class="mt-2 grid grid-cols-3 gap-x-2 gap-y-4">
        <MedFormField orientation="horizontal">
          <MedCheckbox
            v-model="storage"
            variant="accent"
            :disabled="!isEditable"
            :data-test="getTestId('compliance-storage-checkbox')"
            @change="handleSideEffects($event, 'storage')"
          />

          <MedLabel>falsche Lagerung</MedLabel>
        </MedFormField>

        <MedFormField orientation="horizontal">
          <MedCheckbox
            v-model="expired"
            variant="accent"
            :disabled="!isEditable"
            :data-test="getTestId('compliance-expired-checkbox')"
            @change="handleSideEffects($event, 'expired')"
          />

          <MedLabel>FAM verfallen</MedLabel>
        </MedFormField>

        <MedFormField orientation="horizontal">
          <MedCheckbox
            v-model="wrongUse"
            variant="accent"
            :disabled="!isEditable"
            :data-test="getTestId('compliance-wrong-use-checkbox')"
            @change="handleSideEffects($event, 'wronguse')"
          />

          <MedLabel>falsche Anwendung</MedLabel>
        </MedFormField>
      </div>
    </fieldset>
    <!-- end side effects -->

    <!-- dosage Info -->
    <div class="col-span-9 col-start-1">
      <ComponentTextarea
        v-model="deviantMedicationForm.dosage.info"
        label="Hinweise zur abweichenden Anwendung (durch Patienten)"
        maxlength="80"
        rows="2"
        :disabled="!isEditable"
        :privacy-guard="privacyErrors.info ?? undefined"
        :test-id="getTestId('compliance-info')"
        @input="updateComplianceInfo"
      />
    </div>
    <!-- end dosage info -->

    <!-- compliance -->
    <div class="col-span-12 col-start-1 flex items-center space-x-2">
      <ComponentSwitch
        v-model="additionalInformationForm.compliance"
        color="primary"
        :disabled="!isEditable"
        :test-id="getTestId('compliance-is-relevant')"
        @input="updateNonCompliance"
      />

      <template v-if="additionalInformationForm.compliance === true">
        <span :class="{'text-gray-500': !isEditable}">
          Relevanz: im Analyseergebnis nicht berücksichtigen (nicht relevant)
        </span>
      </template>

      <template v-else>
        <span :class="{'text-gray-500': !isEditable}">Relevanz: im Analyseergebnis berücksichtigen</span>
      </template>
    </div>
    <!-- end compliance -->
  </div>
</template>
